import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { 
  Bell, X, Phone, Clock, Shield, 
  FileText, Users, AlertCircle,
  CheckCheck, Smartphone, Trophy, XCircle, Zap, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';

// Icon + color by notification type
const TYPE_CONFIG = {
  approval_required: { icon: Shield, color: 'bg-amber-500' },
  approval_completed: { icon: Shield, color: 'bg-emerald-500' },
  quote_won: { icon: Trophy, color: 'bg-emerald-500' },
  quote_lost: { icon: XCircle, color: 'bg-red-500' },
  task_reminder: { icon: Clock, color: 'bg-blue-500' },
  followup_reminder: { icon: Phone, color: 'bg-violet-500' },
  calendar_event: { icon: CalendarDays, color: 'bg-cyan-500' },
  sequence_step: { icon: Zap, color: 'bg-amber-500' },
  // Legacy reminder types
  daily_outreach: { icon: Phone, color: 'bg-sky-500' },
  daily_followups: { icon: Clock, color: 'bg-amber-500' },
  daily_visibility: { icon: Users, color: 'bg-violet-500' },
  daily_pipeline: { icon: FileText, color: 'bg-emerald-500' },
  weekly_monday_setup: { icon: FileText, color: 'bg-[#1e3a5f]' },
  weekly_friday_review: { icon: CheckCheck, color: 'bg-[#1e3a5f]' },
  approval_requested: { icon: Shield, color: 'bg-amber-500' },
  approval_decided: { icon: Shield, color: 'bg-emerald-500' },
  enquiry_paused_48h: { icon: AlertCircle, color: 'bg-red-500' },
  follow_up_due: { icon: Clock, color: 'bg-amber-500' },
  reminder: { icon: Bell, color: 'bg-slate-500' },
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { 
    notifications, unreadCount, markAsRead, dismiss, dismissAll, refresh,
    pushPermission, requestPushPermission, isPushSupported 
  } = useNotifications();
  const [open, setOpen] = useState(false);

  const handleEnablePush = async () => {
    const result = await requestPushPermission();
    if (result.permission === 'granted') {
      toast.success('Push notifications enabled!');
    } else if (result.permission === 'denied') {
      toast.error('Push notifications blocked. Enable in browser settings.');
    }
  };

  const handleClick = (notification) => {
    markAsRead(notification.id);
    
    // Navigate based on action_url or entity type
    if (notification.action_url) {
      navigate(notification.action_url);
    } else if (notification.type === 'approval_required' || notification.type === 'approval_completed') {
      navigate('/approvals');
    } else if (notification.type === 'quote_won' || notification.type === 'quote_lost') {
      navigate('/enquiries');
    } else if (notification.type === 'task_reminder' || notification.type === 'calendar_event') {
      navigate('/calendar');
    } else if (notification.type === 'followup_reminder') {
      navigate('/pipeline');
    } else if (notification.entity_type === 'approval') {
      navigate('/approvals');
    } else if (notification.entity_type === 'enquiry') {
      navigate(`/enquiry/${notification.entity_id}`);
    }
    
    setOpen(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const now = new Date();
      const diff = now - date;
      if (diff < 60000) return 'Just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className="relative p-2 rounded-xl hover:bg-white/10 transition-all"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-[#1e3a5f]">Notifications</h3>
          {notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={dismissAll}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Clear all
            </Button>
          )}
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {notifications.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {notifications.map(notification => {
                const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG[notification.reminder_type] || { icon: Bell, color: 'bg-slate-500' };
                const Icon = config.icon;
                const color = config.color;
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                      notification.status === 'read' ? 'opacity-60' : ''
                    }`}
                    onClick={() => handleClick(notification)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-slate-800 text-sm">
                            {notification.title}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dismiss(notification.id);
                            }}
                            className="text-slate-400 hover:text-slate-600 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatTime(notification.created_at || notification.scheduled_for)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No notifications</p>
            </div>
          )}
        </div>
        
        <div className="px-4 py-3 border-t border-slate-100 space-y-2">
          {isPushSupported && pushPermission === 'default' && (
            <button
              onClick={handleEnablePush}
              className="w-full flex items-center gap-3 p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
            >
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-800">Enable push notifications</p>
                <p className="text-xs text-emerald-600">Get alerts on your phone</p>
              </div>
            </button>
          )}
          
          {isPushSupported && pushPermission === 'denied' && (
            <div className="text-xs text-slate-400 text-center py-1">
              Push notifications are blocked in browser settings
            </div>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { refresh(); }}
            className="w-full text-xs text-slate-500 hover:text-slate-700"
          >
            Refresh
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
